import { clsx, type ClassValue } from "clsx"
import { extendTailwindMerge } from "tailwind-merge"

const customTwMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      "bg-color": ["bg-button-hover", "bg-button-active", "bg-button-active-hover"],
      "text-color": ["text-button-active-content"],
    },
  },
})

export function cn(...inputs: ClassValue[]) {
  return customTwMerge(clsx(inputs))
}

export function getInitials(name: string) {
  if (!name) return "";
  const parts = name.split(" ");
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function slugify(text: string) {
  return text
    .toString()
    .toLowerCase()
    .trim()
    .normalize('NFD') // separate accent from letter
    .replace(/[\u0300-\u036f]/g, '') // remove accents
    .replace(/\s+/g, '-') // replace spaces with -
    .replace(/[^\w-]+/g, '') // remove all non-word chars
    .replace(/--+/g, '-'); // replace multiple - with single -
}

import { ALL_COUNTRIES, CountryData } from "./countries";

export function getCountryByDDI(value: string): CountryData | null {
  if (!value) return null;
  const numbers = value.replace(/\D/g, "");

  // Sort by DDI length desc to match longest prefix first
  const sorted = [...ALL_COUNTRIES].sort((a, b) => b.ddi.length - a.ddi.length);

  for (const country of sorted) {
    if (numbers.startsWith(country.ddi)) {
      return country;
    }
  }
  return null;
}

export function getCountryIso(countryName: string | null | undefined): string {
  if (!countryName) return "br";
  const found = ALL_COUNTRIES.find(c => c.name.toLowerCase() === countryName.toLowerCase()) ||
    ALL_COUNTRIES.find(c => c.iso.toLowerCase() === countryName.toLowerCase());
  return found?.iso || "br";
}

export function formatPhoneNumber(value: string) {
  if (!value) return "";

  // Format as International if starts with +
  if (value.startsWith("+")) {
    const raw = value.replace(/[^\d+]/g, "");
    const country = getCountryByDDI(raw);

    if (country) {
      const numbers = raw.replace(`+${country.ddi}`, "");
      if (numbers.length > 0) {
        // Use custom mask if available, otherwise simple spacing could be added manually 
        // or just return as is for now since complex global masking is heavy.
        // For Brazil/US we kept masks in the country object if needed.
        if (country.mask) {
          // Very simple masker for now
          let i = 0;
          const formatted = country.mask.replace(/9/g, (_) => numbers[i++] || "");
          return `+${country.ddi} ${formatted.trim()}`;
        }
        return `+${country.ddi} ${numbers}`;
      }
      return `+${country.ddi}`;
    }
    return raw;
  }

  // Fallback to Brazilian Standard (legacy behavior)
  const numbers = value.replace(/\D/g, "");
  if (numbers.length <= 10) {
    return numbers.replace(/(\d{2})(\d{4})(\d{4})/, "($1) $2-$3");
  }
  return numbers.replace(/(\d{2})(\d{5})(\d{4})/, "($1) $2-$3");
}

export function formatDocument(value: string) {
  if (!value) return "";
  const numbers = value.replace(/\D/g, "");
  if (numbers.length === 11) {
    return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  }
  if (numbers.length === 14) {
    return numbers.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
  }
  return value;
}

export function formatCEP(value: string) {
  if (!value) return "";
  const numbers = value.replace(/\D/g, "");
  if (numbers.length === 8) {
    return numbers.replace(/(\d{5})(\d{3})/, "$1-$2");
  }
  return value;
}

export function formatCurrency(value: number | string) {
  if (value === "" || value === undefined || value === null) return "";
  const number = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(number)) return "";
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(number);
}

export function parseCurrency(value: string) {
  if (!value) return 0;
  const str = String(value).trim();
  // PT-BR format: "1.234,56" or "60,00" — comma is decimal separator
  if (str.includes(",")) {
    return Number(str.replace(/[^0-9,-]+/g, "").replace(".", "").replace(",", "."));
  }
  // Float string: "60.00" or "1234.56" — period is decimal separator
  return Number(str.replace(/[^0-9.]+/g, ""));
}

export function compactNumber(value: number) {
  if (value === undefined || value === null) return "0";
  return new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value).toLowerCase();
}
